import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import {
  ProjectOverviewItem,
  ProjectSignals,
  ProjectSubRow,
  ReifegradGewichte,
  Windpark,
} from "../../../../types";
import ProjectSignalBadges from "./ProjectSignalBadges";
import { signalBadges } from "../../../../utils/projectFocus";
import Button from "../../../common/inputs/Button";
import ActionsMenu from "../../../common/overlays/ActionsMenu";
import ProjectTable from "../../../tables/ProjectTable";
import Section from "../../../layout/Section";
import NewProjectDialog from "./NewProjectDialog";
import VereinheitlichenDialog from "./VereinheitlichenDialog";
import ProgressDialog from "../../../common/feedback/ProgressDialog";
import ZugriffDialog from "../../../common/overlays/zugriff/ZugriffDialog";
import { usePagePermissions } from "../../../../auth/hooks/usePagePermissions";
import { useWirtschaftlichkeitSimulation } from "../hooks/useWirtschaftlichkeitSimulation";
import { formatNumber } from "../../../../utils/formatters";
import { colors } from "../../../../styles/colors";

interface ProjectsSectionProps {
  projects: ProjectOverviewItem[];
  onProjectDeleted: (projectId: string) => Promise<void>;
  onProjectExport: (projectId: string) => Promise<void>;
  exportingProjectId: string | null;
  onProjectCreated: (windpark: Windpark) => void;
  wirtschaftlichkeitErklaerung?: string;
  reifegradErklaerung?: string;
  reifegradGewichte?: ReifegradGewichte | null;
  /** Projekt im Fokus – aufgeklappt und auf Karte und Termine durchgereicht */
  focusProjectId: string | null;
  onFocusChange: (projectId: string | null) => void;
  /** Inhalt der aufgeklappten Zeile unter den Unterzeilen, etwa ein Hinweis; `null` für keinen */
  renderFocusDetail: (projectId: string) => React.ReactNode;
  /** Unterzeilen des aufgeklappten Projekts, etwa seine Anträge */
  focusSubRows?: (projectId: string) => ProjectSubRow[];
  /** Problemsignale je Projekt-ID */
  signals: Record<string, ProjectSignals>;
  /** Höhe des Scrollbereichs der Tabelle im festen Arbeitsbereich */
  tableMaxHeight?: string;
}

const ProjectsSection: React.FC<ProjectsSectionProps> = ({
  projects,
  onProjectDeleted,
  onProjectExport,
  exportingProjectId,
  onProjectCreated,
  wirtschaftlichkeitErklaerung,
  reifegradErklaerung,
  reifegradGewichte,
  focusProjectId,
  onFocusChange,
  renderFocusDetail,
  focusSubRows,
  signals,
  tableMaxHeight,
}) => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [vereinheitlichenOffen, setVereinheitlichenOffen] = useState(false);
  const [zugriffProjekt, setZugriffProjekt] =
    useState<ProjectOverviewItem | null>(null);
  const { canEdit } = usePagePermissions("project-overview");

  const {
    fortschritt,
    abbrechen,
    simulieren,
    zuruecksetzen,
    simulation,
    angezeigteProjekte,
    simulierteIds,
  } = useWirtschaftlichkeitSimulation(projects);

  // Externe gehören keinem Mandanten an, in dem sie Projekte anlegen könnten.
  const nurExterneProjekte =
    projects.length > 0 && projects.every((p) => !p.ist_intern);
  const darfAnlegen = canEdit && !nurExterneProjekte;

  // Spalten, die mehrere Bereiche zusammenfassen, bleiben Externen verborgen; die
  // Wirtschaftlichkeits-Kennzahlen hängen am entsprechenden Bereich. Fail-closed: Sobald ein
  // Projekt der Liste das Recht nicht trägt, verschwindet die Spalte für alle.
  const spaltenSichtbar = {
    wirtschaftlichkeit: projects.every(
      (p) => p.meine_rechte?.["kalkulation.wirtschaftlichkeit"] !== undefined,
    ),
    aggregate: projects.every((p) => p.ist_intern),
  };

  // Vereinheitlichen setzt an denselben Kennzahlen an wie die Wirtschaftlichkeits-Spalte –
  // wer die nicht sehen darf, bekommt auch den Button nicht. Lesen genügt: Gerechnet wird
  // nur für die Anzeige, gespeichert wird nichts.
  const darfSimulieren =
    spaltenSichtbar.wirtschaftlichkeit && projects.length > 0;

  /**
   * Welche Spalten während einer Simulation abgesetzt dargestellt werden. IRR und Score
   * ändern sich immer; Zins und Zuschlag nur, wenn der jeweilige Wert vereinheitlicht wurde.
   */
  const simulierteSpalten = useMemo(() => {
    if (!simulation) return new Set<string>();
    const spalten = new Set(["wirtschaftlichkeit", "wirtschaftlichkeit_irr"]);
    if (simulation.werte.zuschlagswert !== undefined) {
      spalten.add("wirtschaftlichkeit_zuschlag");
    }
    if (simulation.werte.fremdkapitalzins !== undefined) {
      spalten.add("wirtschaftlichkeit_zins");
    }
    return spalten;
  }, [simulation]);

  /**
   * Was gerade simuliert wird – am Zurücksetzen-Button. Seit der Hinweisleiste über der
   * Tabelle ist das der einzige Ort, an dem die unterstellten Werte noch nachlesbar sind.
   */
  const simulationsHinweis = useMemo(() => {
    if (!simulation) return "";
    const teile: string[] = [];
    if (simulation.werte.zuschlagswert !== undefined) {
      teile.push(
        `Zuschlagswert ${formatNumber(simulation.werte.zuschlagswert, 2)} ct/kWh`,
      );
    }
    if (simulation.werte.fremdkapitalzins !== undefined) {
      teile.push(
        `Kreditzins ${formatNumber(simulation.werte.fremdkapitalzins, 1)} % p.a.`,
      );
    }
    let text = `Simulation: ${teile.join(", ")} – nicht gespeichert.`;
    if (simulation.nichtSimulierbar > 0) {
      text += ` ${simulation.nichtSimulierbar} Projekt${
        simulation.nichtSimulierbar === 1 ? "" : "e"
      } nicht simulierbar und unverändert.`;
    }
    return text;
  }, [simulation]);

  const handleProjectNavigate = (projectId: string) => {
    navigate(`/project/${projectId}/overview`);
  };

  return (
    <>
      <Section
        title="Windparks"
        noPadding={true}
        actions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {simulation && (
              // Solange eine Simulation läuft, steht „Zurücksetzen“ als eigener Button in der
              // Kopfzeile statt im Menü: Er ist der einzige sichtbare Hinweis auf die
              // Simulation und der Weg zurück.
              <Tooltip title={simulationsHinweis}>
                <span>
                  <Button
                    variant="secondary"
                    startIcon={
                      <CloseIcon sx={{ color: colors.status.error.main }} />
                    }
                    onClick={zuruecksetzen}
                  >
                    Zurücksetzen
                  </Button>
                </span>
              </Tooltip>
            )}
            {!nurExterneProjekte && (
              <Tooltip
                title={
                  !darfAnlegen ? "Fehlende Berechtigung: projects:write" : ""
                }
                disableHoverListener={darfAnlegen}
              >
                <span>
                  {/* Hauptaktion der Kachel, gefüllt (components/CLAUDE.md) */}
                  <Button
                    variant="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setDialogOpen(true)}
                    disabled={!darfAnlegen}
                  >
                    Projekt anlegen
                  </Button>
                </span>
              </Tooltip>
            )}
            <ActionsMenu
              ariaLabel="Weitere Aktionen zu den Windparks"
              items={
                darfSimulieren && !simulation
                  ? [
                      {
                        label: "Vereinheitlichen",
                        icon: <TuneIcon fontSize="small" />,
                        onClick: () => setVereinheitlichenOffen(true),
                      },
                    ]
                  : []
              }
            />
          </Box>
        }
      >
        <ProjectTable
          projects={angezeigteProjekte}
          onProjectOpen={handleProjectNavigate}
          expandedId={focusProjectId}
          onExpand={onFocusChange}
          renderExpandedRow={renderFocusDetail}
          subRows={focusSubRows}
          renderNameSuffix={(id) => (
            <ProjectSignalBadges badges={signalBadges(signals[id])} />
          )}
          maxHeight={tableMaxHeight}
          onProjectDeleted={onProjectDeleted}
          onProjectExport={onProjectExport}
          exportingProjectId={exportingProjectId}
          onProjectShare={(id) =>
            setZugriffProjekt(projects.find((p) => p.id === id) ?? null)
          }
          spaltenSichtbar={spaltenSichtbar}
          selectedId={focusProjectId ?? undefined}
          simulierteIds={simulierteIds}
          simulierteSpalten={simulierteSpalten}
          wirtschaftlichkeitErklaerung={wirtschaftlichkeitErklaerung}
          reifegradErklaerung={reifegradErklaerung}
          reifegradGewichte={reifegradGewichte}
        />
      </Section>

      <VereinheitlichenDialog
        open={vereinheitlichenOffen}
        onClose={() => setVereinheitlichenOffen(false)}
        onStarten={(werte) => {
          setVereinheitlichenOffen(false);
          void simulieren(werte);
        }}
      />

      <ProgressDialog
        open={fortschritt.laeuft}
        titel="Projekte werden neu berechnet"
        aktuell={fortschritt.aktuell}
        gesamt={fortschritt.gesamt}
        label={fortschritt.label}
        onAbbrechen={abbrechen}
      />

      <NewProjectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onProjectCreated={onProjectCreated}
      />

      {zugriffProjekt && (
        <ZugriffDialog
          projectId={zugriffProjekt.id}
          projectName={zugriffProjekt.name}
          open
          onClose={() => setZugriffProjekt(null)}
        />
      )}
    </>
  );
};

export default ProjectsSection;
